<template>
  <div :class="{ disableChat: isInputDisabled }">
    <div v-if="alertMessage !== ''" class="alert too-long-alert">{{ alertMessage }}</div>
    <!-- File upload progress -->
    <div v-if="isShownUploadProgress" class="progressContainer">
      <span>{{ $t('uploading') }}</span>
      <div>
        <span class="fileName">{{ uploadFileName }}</span>
        <div class="progress">
          <div class="progress-value" :style="{ width: fileUploadProgress + '%' }"></div>
        </div>
      </div>
      <a href="#" class="close" @click.prevent="abortUpload">
        <i class="fa fa-times"></i>
      </a>
    </div>
    <div v-else class="inputContainer">
      <!-- Shortcut menu -->
      <shortcut-menu
        v-if="shortcutMenu.length"
        :menu-items="shortcutMenu"
        :shortcut-menu-class="shortcutMenuClass"
        :is-kiosk="isKiosk"
        :assistant-id="assistantId"
        :is-disabled="isSendingMessage"
      />

      <!-- File upload button -->
      <div v-if="showFileUploadIcon" class="chatInputIconContainer" :class="{ centerContainer: isAudioInputToggle }">
        <a @click.stop.prevent="fileUpload">
          <i class="fas fa-image" :class="{ disabled: isSendingMessage }" aria-hidden="true"></i>
        </a>
      </div>
      <!-- Mic Icon Template -->
      <div
        v-if="isAudioInputAvailable && isAudioInputToggle"
        :class="['mic', recordingStatusClass, isRecPermDenied ? 'permDenied' : '']"
      >
        <div id="on_hold" class="recorder_on_hold" :class="inputIconClass">
          <div
            class="hint"
            :class="[isSTTEnabled ? 'webkitSTT' : '', `${recordingMode}RecordingMode`, recordingHintClass]"
          >
            <span class="hint_content">
              <span class="hold">{{ recordingStartHintLabel }}</span>
              <span class="tap">{{ $t('tap_to_start') }}</span>
              <span v-if="isRecPermDenied">{{ $t('microphone_permission_denied') }}</span>
              <span class="record">
                <div class="record-progress">
                  <div></div>
                </div>
                <i class="fas fa-circle blink_me" aria-hidden="true"></i>
                {{ $t('recording') }}
              </span>
              <span class="analyse">
                <i class="fas fa-cloud blink_me" aria-hidden="true"></i>
                {{ $t('analyzing_audio') }}
              </span>
              <span class="short">
                <i class="fas fa-exclamation-circle" aria-hidden="true"></i>
                {{ $t('record_tip') }}
              </span>
            </span>
          </div>

          <!-- Microphone UI for simple tap interaction -->
          <span
            v-if="
              isSTTEnabled ||
                userAgentDetails.isSafari ||
                (isUserMediaAvailable && recordingMode === configRecordingMode.TAP)
            "
            id="mic_recording_bar"
            class="mic_recording_bar"
            @click.prevent.stop="startAudioSpeechToText"
          >
            <span class="mic_container">
              <i class="fas fa-microphone" aria-hidden="true"></i>
              <i class="fas fa-microphone-slash" aria-hidden="true"></i>
            </span>
          </span>
          <!-- Microphone UI for tap and hold interaction -->
          <span
            v-else-if="isUserMediaAvailable && recordingMode === configRecordingMode.LONG_PRESS"
            id="mic_recording_bar"
            v-longclick.prevent.stop="{
              stopShortClick: stopShortRecording,
              startClick: startRecording,
              stopClickWithDelay: stopRecording,
              stopClick: stopRecordingDisplay
            }"
            class="mic_recording_bar"
          >
            <span class="mic_container">
              <i class="fas fa-microphone" aria-hidden="true"></i>
              <i class="fas fa-microphone-slash" aria-hidden="true"></i>
            </span>
          </span>
          <span class="keyboard_link" @click.prevent="toggleAudioInput">
            <i class="fas fa-keyboard" aria-hidden="true"></i>
          </span>
        </div>
      </div>
      <div v-else class="keyboard">
        <!-- Input box layout -->
        <div class="chat-input">
          <div class="enter-message">
            <div class="right chat-input">
              <textarea
                id="input_chat"
                v-model.trim="userMessage"
                v-focus
                :maxlength="counterSettings.MAX_LENGTH"
                :data-placeholder="placeholder"
                :placeholder="placeholder"
                :class="{ inputDisabled: isInputDisabled }"
                :disabled="isInputDisabled"
                type="text"
                autocomplete="off"
                rows="1"
                @input="captureInput"
                @click="initAudio"
                @focus="handleFocus"
                @keydown.enter="handleEnterKeyDown"
              ></textarea>
              <div class="waiting-message">{{ $t('composer_wait') }}</div>
              <div v-if="isCounterShown" class="counterContainer" :class="warningClass">
                <p>{{ userMessage.length }}/{{ counterSettings.MAX_LENGTH }}</p>
              </div>
              <template v-if="isAudioInputAvailable">
                <div class="toggleContainer">
                  <a class="button mic_link" @click.prevent="toggleAudioInput">
                    <i class="fas fa-microphone" aria-hidden="true"></i>
                  </a>
                </div>
              </template>

              <a
                v-if="userMessage && !isSendingMessage"
                href="#"
                class="button send"
                @click.prevent="sendMessage(inputTypes.KEYBOARD)"
              >
                <i class="fa fa-paper-plane"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import audioLib from '../lib/audio'
import videoLib from '../lib/video'
import { mapGetters } from 'vuex'
import {
  sanitizeHTML,
  isUserMediaAvailable,
  getUserMedia,
  correctMessageBasedOnWordRules,
  scrollToNewMessage
} from '../lib/utils'
import MobileDetect from 'mobile-detect'
import Recorder from '../lib/recorder'
import { getUserAgentObject } from '../lib/ua'
import { fileDialog } from '../lib/fileDialog'

import config from '../config/chatInput'
import configDefault from '../config'

// Style
import '../assets/styles/chatInput.css'

// Components
import ShortcutMenu from '../components/shortcutMenu.vue'

let alertTimeout = null

export default {
  name: 'ChatInput',
  components: { ShortcutMenu },
  props: {
    isSTTEnabled: { type: Boolean, default: false },
    isSTTContinuous: { type: Boolean, default: false },
    isAudioRecordingVisible: { type: Boolean, default: configDefault.IS_AUDIO_RECORDING_VISIBLE },
    isAudioMobileFront: { type: Boolean, default: configDefault.IS_AUDIO_MOBILE_FRONT },
    isAudioDesktopFront: { type: Boolean, default: configDefault.IS_AUDIO_DESKTOP_FRONT },
    isRatingPanelDisplayed: { type: Boolean, default: false },
    isAdmin: { type: Boolean, default: false },
    isKiosk: { type: Boolean, default: false },
    userAgentDetails: { type: Object, default: () => {} }
  },
  data() {
    const md = new MobileDetect(navigator.userAgent)
    return {
      isSTTActive: false,
      isRecording: false,
      isAnalyzing: false,
      showRecordTip: false,
      isSTTInError: false,
      isUserMediaAvailable: false,
      userMessage: '',
      webkitSTT: null,
      isMobile: md.mobile() !== null || md.tablet() !== null,
      mediaRecorder: null,
      audioStream: null,
      audioContext: null,
      isAudioInputToggle: this.isKiosk,
      recordTimer: null,
      recordingMode: config.RECORDING_MODES.LONG_PRESS,
      configRecordingMode: config.RECORDING_MODES,
      isRecPermDenied: false,
      storedWebkitMessages: [],
      inputTypes: configDefault.INPUT_TYPES,
      counterSettings: config.COUNTER_SETTINGS,
      alertMessage: ''
    }
  },
  computed: {
    isAudioInputAvailable: function() {
      /*
       * this.isSTTEnabled === true means that
       * we have yelda settings authorization to use microphone (isRecordingOnInSettings true)
       * current browser support webkitSpeechRecognition (Chrome only) and thus is able to record audio and do speech to text
       * isUserMediaAvailable ==true means that
       * we have yelda settings authorization to use microphone (if isAudioRecordingVisible is false, isUserMediaAvailable is forced to false)
       * current browser support getUserMedia and thus is able to record audio
       */
      return this.isSTTEnabled || this.isUserMediaAvailable
    },
    placeholder: function() {
      return this.isSTTActive ? this.$t('input_placeholder_stt') : this.$t('input_placeholder')
    },
    recordingStatusClass: function() {
      if (this.showRecordTip) {
        return 'longer'
      } else if (this.isAnalyzing) {
        return 'analyzing'
      } else if (this.isRecording) {
        return this.recordingMode === config.RECORDING_MODES.TAP ? 'recording stop' : 'recording'
      } else if (this.isSTTActive) {
        return 'recording continues'
      }

      return ''
    },
    isSTTBusy: function() {
      return this.isRecording || this.isAnalyzing || this.isSTTActive
    },
    isCounterShown: function() {
      return this.userMessage.length >= this.counterSettings.COUNTER_SHOWN_LENGTH
    },
    warningClass: function() {
      if (this.userMessage.length >= this.counterSettings.MAX_LENGTH) {
        return 'red'
      }

      return this.counterSettings.MAX_LENGTH - this.userMessage.length <= this.counterSettings.WARNING_REMAINING_LENGTH
        ? 'orange'
        : ''
    },
    ...mapGetters({
      isTTSEnabled: 'isTTSEnabled',
      locale: 'locale',
      connected: 'connected',
      lastScrollHeight: 'lastScrollHeight',
      isInputEnabled: 'isInputEnabled', // boolean that indicate that the user cant type in the input.
      // is set to true if connection is lost or when isMobile && isSendingMessage or form component is being rendered
      // difference between isInputEnabled and isSendingMessage is because we dont want to disable input on mobile while waiting for answer to avoid mobile keyboard to go up and down
      // even though in mobile if the form component is rendered the chat input will be disabled
      // if true, user can't send new message and send button is hidden
      isSendingMessage: 'isSendingMessage', // boolean that indicate that the user has sent a message and is expecting an answer from the webchat API
      // File Upload option is visible if isFileUploadActivated is set to true
      isFileUploadActivated: 'isFileUploadActivated',
      isShownUploadProgress: 'isShownUploadProgress',
      fileUploadProgress: 'fileUploadProgress',
      uploadFileName: 'uploadFileName',
      isAudioPlaying: 'isAudioPlaying',
      nlpWordRules: 'nlpWordRules',
      shortcutMenu: 'shortcutMenu',
      assistantId: 'assistantId'
    }),
    isInputDisabled: function() {
      return !this.connected || !this.isInputEnabled
    },
    isAudioActivatedByDefault: function() {
      return (this.isMobile && this.isAudioMobileFront) || this.isAudioDesktopFront || this.isKiosk
    },
    showFileUploadIcon: function() {
      return this.isFileUploadActivated && !this.isKiosk
    },
    recordingStartHintLabel() {
      // On safari, we don't need to tap and hold to record an audio, so adapt the label to that
      return this.userAgentDetails.isSafari ? this.$t('tap_to_start') : this.$t('tap_and_hold')
    },
    inputIconClass() {
      /**
       * These class used to position the record button at the center of the chat input
       * based on number of icons displayed on the left side of the record button
       */
      const nbIcons = this.getNumberOfInputIcons()
      if (!nbIcons) {
        return ''
      }
      return nbIcons === 1 ? 'one-icon' : 'two-icons'
    },
    shortcutMenuClass() {
      return this.isAudioInputToggle ? 'audioMode' : 'keyboardMode'
    },
    recordingHintClass() {
      /**
       * These class used to position the hint block either right side (when no icons present)
       * or left side (when icon present) based on the existence of input icons (file upload icon, menu icon)
       */
      const nbIcons = this.getNumberOfInputIcons()
      return nbIcons > 0 ? 'right-side' : 'left-side'
    }
  },
  watch: {
    isSTTContinuous: function(newValue, oldValue) {
      if (!this.isSTTEnabled) {
        return false
      }

      // User activate continuous conversation
      if (!oldValue && newValue && this.webkitSTT && !this.isSTTActive) {
        this.isAudioInputToggle = true
        this.webkitSTT.start()
      }

      // User deactivate continuous conversation
      if (oldValue && !newValue && this.webkitSTT) {
        this.webkitSTT.abort()
        this.isSTTActive = false
      }
    },
    isAudioRecordingVisible: function() {
      this.isAudioInputToggle = this.isAudioRecordingVisible && this.isAudioActivatedByDefault
      this.setIsUserMediaAvailable()
    },
    isCounterShown: function() {
      this.handleAutoExpand(document.getElementById('input_chat'))
    },
    isSTTBusy: function(value) {
      this.$store.dispatch('set_STT_status', value)
    },
    isAudioPlaying: function(value) {
      if (this.isSTTContinuous && this.isSTTEnabled) {
        // If it's true, audio is playing, so we disable STT
        if (value) {
          this.webkitSTT.abort()
          this.isSTTActive = false
        } else if (!value && !this.isSTTActive) {
          this.webkitSTT.start()
        }
      }
    },
    isSendingMessage: function(value) {
      if (this.isSTTContinuous && !this.isTTSEnabled && !value) {
        this.webkitSTT.start()
      }
    }
  },
  mounted: function() {
    this.initSpeechToText()
    if (this.isSTTContinuous && this.webkitSTT) {
      this.webkitSTT.start()
    }
  },
  created: function() {
    this.setIsUserMediaAvailable()

    if (getUserAgentObject().isFirefox) {
      this.recordingMode = config.RECORDING_MODES.TAP
    }

    if (this.isSTTEnabled) {
      this.recordingMode = config.RECORDING_MODES.WEBKIT_CONTINUES
    }
  },
  methods: {
    captureInput(event) {
      if (this.isRatingPanelDisplayed) {
        this.$emit('hideRatingPanel')
      }
      this.userMessage = event.target.value.trim()

      this.handleAutoExpand(event.target)
      // If the user is typing, we stop the TTS
      this.$emit('setTTSStatus', false)
    },
    startAudioSpeechToText(event) {
      // Return when voice/text input disabled
      if (this.isInputDisabled) {
        return
      }

      if (this.isSTTEnabled) {
        this.startSpeechToText(event)
      } else {
        this.startRecording()
      }

      scrollToNewMessage()
    },
    /**
     * ************************************* WEBCHAT methods *******************************
     */
    sendMessage: function(inputType) {
      if (this.isSendingMessage) {
        return false
      }

      this.initAudio()

      /**
       * Check New line & empty space at both the end of the string
       * ^ asserts position at start of the string
       * \r matches a carriage return (ASCII 13)
       * \n matches a line-feed (newline) character (ASCII 10)
       * \s matches any whitespace character
       * \t matches a tab character (ASCII 9)
       * + Quantifier — Matches between one and unlimited times, as many times as possible, giving back as needed (greedy)
       * $ asserts position at the end of the string, or before the line terminator right at the end of the string
       */
      this.userMessage = this.userMessage.replace(/^[\r\n\s\t]+|[\r\n\s\t]+$/g, '')
      this.userMessage = sanitizeHTML(this.userMessage)

      // Cut the mic if it's listening
      if (this.isSTTEnabled && !this.isSTTContinuous && this.webkitSTT) {
        this.webkitSTT.abort()
      }

      if (this.userMessage.trim() !== '') {
        // correct the user voice message based on nlp word rules
        if (inputType === this.inputTypes.VOICE && this.nlpWordRules && this.nlpWordRules.length) {
          this.userMessage = correctMessageBasedOnWordRules(this.userMessage, this.nlpWordRules)
        }

        const message = [
          {
            messageType: 'message',
            mainContent: this.userMessage,
            inputType: inputType,
            intentAnalysed: null,
            actionDeduced: null,
            context: null
          }
        ]

        this.userMessage = ''
        this.initializeAutoExpand(document.getElementById('input_chat'))
        this.$store.dispatch('socket_userMessage', message)
      }
    },
    /**
     * Handles the focus event on the input box
     */
    handleFocus: function(event) {
      // If it is mobile scroll to bottom of the conversation
      if (this.isMobile) {
        const chatList = document.getElementById('chatList')

        // SetTimeout added to handle the keyboard layout displaying delay
        setTimeout(() => {
          chatList.scrollTop = chatList.scrollHeight
        }, 500)
      }

      this.handleAutoExpand(event.target)
    },
    initializeAutoExpand: function(element) {
      // To auto expand the textarea the scrollHeight is saved in baseScrollHeight
      if (!element) {
        return
      }

      element.value = ''
      element.rows = 1
    },
    handleAutoExpand: function(element) {
      // The Number of rows calculated from the baseScrollHeight to auto expand the textarea from the content
      if (!element) {
        return
      }

      element.style.height = 'auto'
      element.style.height = element.scrollHeight + 'px'
    },

    /**
     * ************************************* WEBCHAT AUDIO methods *******************************
     */

    /**
     * This function is triggered on both input click and keypress
     * so we have more chances to get the audio authorization
     */
    initAudio: function() {
      if (!this.isTTSEnabled) {
        return false
      }

      audioLib.ensureInit()
    },
    setIsUserMediaAvailable: function() {
      if (this.isAudioRecordingVisible) {
        isUserMediaAvailable()
          .then(() => {
            this.isUserMediaAvailable = audioLib.checkMicrophoneHeaderPermissionGiven()

            // Since in FB/Messenger Webview browser webkitSpeechRecognition & getUserMedia are not working
            // We hide the recording button
            if (getUserAgentObject().isFBBrowser) {
              this.isUserMediaAvailable = false
            }
          })
          .catch(() => {
            console.warn('isUserMediaAvailable false')
          })
      } else {
        this.isUserMediaAvailable = false
      }
    },

    /**
     * ************************************* WEBCHAT WEBKIT AUDIO methods *******************************
     */

    /**
     * Init the webkit speech to text
     **/
    initSpeechToText: function() {
      if (!this.isSTTEnabled) {
        return false
      }

      this.webkitSTT = new webkitSpeechRecognition() // eslint-disable-line

      // Init webkitSpeechRecognition

      /* We are facing few issues, since interimResults is set to true

        In Mobile Chrome / Samsung Internet Browser when interimResults set to true, the results have isFinal returned as true for every iterations of result, which leads to webkitSTT stoped in few seconds.

        So we set the interimResults to false for mobile
      */
      this.webkitSTT.continuous = true
      this.webkitSTT.interimResults = !this.isMobile
      this.webkitSTT.lang = this.locale.replace('_', '-')

      // When the browser start listening
      this.webkitSTT.onstart = function() {
        this.isSTTActive = true
        scrollToNewMessage()
        // Stop listening after 5s without interaction
        this.createAbortTimeout()
      }.bind(this)

      // When there is an error with the module
      this.webkitSTT.onerror = function(event) {
        switch (event.error) {
          case 'no-speech':
            // stop listening
            this.webkitSTT.abort()
            return false
          case 'aborted':
            // Do nothing, it's not an error
            return false
          default:
            console.error('webkitSpeechRecognition error: ' + event.error)
            this.isSTTActive = false
            this.isSTTInError = true
        }
      }.bind(this)

      // When the browser stop listening
      this.webkitSTT.onend = function() {
        clearTimeout(this.abortTimeout)
        this.isSTTActive = false
      }.bind(this)

      this.webkitSTT.onspeechend = function() {
        this.sendConvertedMessage()
      }.bind(this)

      // When we receive a result or a fragment of result
      this.webkitSTT.onresult = function(event) {
        // When receiving result, clear aborttimeout so it does not stop when user is talking
        clearTimeout(this.abortTimeout)

        if (typeof event.results === 'undefined') {
          this.webkitSTT.onend = null

          if (!this.isSTTContinuous && this.webkitSTT) {
            this.webkitSTT.abort()
          }
          return false
        }

        // If the input is disabled, do nothing
        if (!this.isInputEnabled || this.isSendingMessage) {
          return false
        }

        // Stop the audio/video before starting the speech-to-text
        audioLib.stop(this.isTTSEnabled)
        videoLib.pauseVideos()

        let textFromSpeech = ''

        let isFinal = false

        for (let index = event.resultIndex; index < event.results.length; index++) {
          textFromSpeech += event.results[index][0].transcript

          if (event.results[index].isFinal) {
            isFinal = true
            break
          }
        }

        this.userMessage = `${this.storedWebkitMessages.join(' ')} ${textFromSpeech}`
        this.$store.dispatch('set_webkit_stt_message', this.userMessage)

        if (isFinal) {
          this.userMessage = textFromSpeech
          this.storedWebkitMessages.push(this.userMessage)
          this.sendConvertedMessage()
        }
      }.bind(this)
    },
    createAbortTimeout() {
      clearTimeout(this.abortTimeout)
      this.abortTimeout = setTimeout(() => {
        this.webkitSTT.abort()
      }, config.MAX_LISTENING_TIMEOUT)
    },
    /**
     * Start a speech to text recognition
     */
    startSpeechToText: function(event) {
      this.stopEventPropagation(event)

      // If speech-to-text is disabled, exit the function
      if (!this.isSTTEnabled) {
        console.warn('isSTTEnabled', this.isSTTEnabled)
        return false
      }

      // If speech-to-text was not initialized yet, initialize it
      if (!this.webkitSTT) {
        this.initSpeechToText()
      }

      // If the speech-to-text is in error, we do nothing
      if (this.isSTTInError) {
        console.warn('isSTTInError', this.isSTTInError)
        return false
      }

      // If the speech-to-text is already active, stop it
      if (this.isSTTActive) {
        this.webkitSTT.abort()
        return false
      }

      if (this.isSTTContinuous && this.isAudioPlaying) {
        // Stop the audio/video before starting the speech-to-text
        audioLib.stop(this.isTTSEnabled)
        videoLib.pauseVideos()
        return
      }

      // Stop the audio/video before starting the speech-to-text
      audioLib.stop(this.isTTSEnabled)
      videoLib.pauseVideos()

      this.webkitSTT.start()
      return false
    },

    sendConvertedMessage: function() {
      this.webkitSTT.abort()

      this.userMessage = this.clipUserMessage(this.storedWebkitMessages.join(' '))
      this.storedWebkitMessages = []
      this.$store.dispatch('set_webkit_stt_message', '')

      if (this.userMessage !== '') {
        this.sendMessage(this.inputTypes.VOICE)
      }
    },
    stopEventPropagation: function(event) {
      event.preventDefault && event.preventDefault()
      event.stopPropagation && event.stopPropagation()
      event.cancelBubble = true
      event.returnValue = false
    },

    /**
     * ************************************* WEBCHAT getUserMedia AUDIO methods (non Chrome browsers) *******************************
     */
    startRecording: function() {
      // Return when voice/text input disabled
      if (this.isInputDisabled) {
        return
      }

      try {
        if (this.isRecording) {
          this.stopRecording()
          return
        }

        if (this.isSTTBusy) {
          return false
        }

        this.showRecordTip = false

        if (!navigator.permissions) {
          this.recordUserAudio()
          return false
        }

        navigator.permissions
          .query({ name: 'microphone' })
          .then(result => {
            if (result.state === 'granted' || result.state === 'prompt') {
              // If the state is granted the permission we can directly access the microphone
              // if the state is prompt user have to give access
              this.recordUserAudio()
            } else if (result.state === 'denied') {
              // The permission to access microphone either denied in this case do nothing
              this.isRecPermDenied = true
              return
            }

            // handling the permission status
            result.onchange = event => {
              if (event.target.state === 'denied') {
                this.isRecPermDenied = true
                return
              } else if (event.target.state === 'granted') {
                this.recordUserAudio()
              }
            }
          })
          .catch(() => {
            // Firefox throws error while we trying to get the navigator permission for microphone sometimes
            // On this case ask for getUserMedia
            // Ref: https://stackoverflow.com/questions/53147944/firefox-permission-name-member-of-permissiondescriptor-camera-is-not-a-vali
            this.recordUserAudio()
          })
      } catch (error) {
        console.error('startRecording error', error)
      }
    },
    startRecordTimer: function() {
      this.recordTimer = setTimeout(() => {
        this.stopRecording()
      }, config.MAX_RECORDING_TIME)
    },
    stopRecordTimer: function() {
      if (this.recordTimer) {
        clearTimeout(this.recordTimer)
        this.recordTimer = null
      }
    },
    recordUserAudio: function() {
      // If media is already recording stop it
      if (this.isSTTActive || this.mediaRecorder !== null) {
        this.stopRecording()
        return
      }

      getUserMedia({ audio: true, video: false })
        .then(stream => {
          this.isRecPermDenied = false
          this.isRecording = true
          this.stopRecordTimer()

          // Stop the audio playback before starting recording
          audioLib.stop(this.isTTSEnabled)
          // Pause the video
          videoLib.pauseVideos()

          this.startRecordTimer()

          if (!this.isRecording) {
            stream.getAudioTracks()[0].stop()
            return
          }

          this.isSTTActive = true

          this.destroyAudioContext()

          this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
          this.audioStream = stream

          this.mediaRecorder = new Recorder(this.audioContext)

          this.mediaRecorder.init(stream)
          this.mediaRecorder.start()
        })
        .catch(error => {
          this.isSTTActive = false
          this.isRecording = false
          this.mediaRecorder = null
          this.audioStream = null
          this.isRecPermDenied = true

          console.error('getUserMedia error: ' + error)
        })
    },
    stopRecordingDisplay: function() {
      this.isAnalyzing = true
    },
    stopRecording: function() {
      this.stopRecordTimer()
      // stopRecording is triggered on mouseup or mouseout
      // mouseout is there to handle user clicking and moving the mouse with click still down : mouseup is never called in that case
      if (!this.isRecording) {
        return false
      }
      this.isSTTActive = false
      this.isRecording = false

      this.destroyAudioContext()

      if (this.mediaRecorder) {
        this.mediaRecorder
          .stop()
          .then(blob => {
            this.getTextFromGoogle(blob)
          })
          .finally(() => {
            this.clearRecording()
          })
      } else {
        this.isAnalyzing = false
        this.clearRecording()
      }
    },
    stopShortRecording: function() {
      this.stopRecordTimer()

      if (!this.isRecording) {
        return false
      }
      // Show the record tip
      this.showRecordTip = true

      // hide the record tip after 1 sec
      setTimeout(() => {
        this.showRecordTip = false
      }, 2000)

      this.mediaRecorder = null
      this.stopRecording()
    },
    destroyAudioContext() {
      this.audioStream && this.audioStream.getAudioTracks()[0].stop()
      this.audioStream = null

      if (this.audioContext !== null) {
        this.audioContext.close()
        this.audioContext = null
      }
    },
    clearRecording: function() {
      this.mediaRecorder = null
      this.isSTTActive = false
      this.isRecording = false
    },
    getTextFromGoogle(blob) {
      const filename = new Date().toISOString()
      const xhr = new XMLHttpRequest()
      xhr.onload = event => {
        this.isAnalyzing = false

        if (event.target.status === 200 && event.target.response) {
          try {
            const responseObject = JSON.parse(event.target.response)
            this.userMessage = this.clipUserMessage(responseObject && responseObject.text)
          } catch (error) {
            this.userMessage = this.clipUserMessage(event.target.response || '')
          }
          this.sendMessage(this.inputTypes.VOICE)
        }
      }

      xhr.onerror = () => {
        this.isAnalyzing = false
      }

      const formData = new FormData()
      const apiRoot = process.env.GET_SPEECH_ENDPOINT || 'https://microservices.yelda.ai'
      formData.append('audio_data', blob, filename)
      formData.append('locale', this.locale.replace('_', '-'))
      xhr.open('POST', `${apiRoot}/speechToText`, true)
      xhr.send(formData)
      this.isAnalyzing = true
    },
    toggleAudioInput: function(event) {
      scrollToNewMessage()

      // Return when voice/text input disabled
      if (this.isInputDisabled) {
        return
      }

      // Stop the current recording stream if any
      if (this.isAudioInputToggle) {
        if (this.isSTTEnabled) {
          // Force stopping the webkitSTT
          if (this.webkitSTT) {
            this.webkitSTT.abort()
            this.isSTTActive = false
          }
        } else {
          this.stopRecordTimer()
          this.destroyAudioContext()
          this.clearRecording()
        }
      } else {
        if (this.isSTTContinuous && !this.isAudioPlaying && !this.isSTTActive) {
          this.webkitSTT.start()
        } else if (this.isSTTEnabled) {
          this.startSpeechToText(event)
        }
        if (this.isRatingPanelDisplayed) {
          this.$emit('hideRatingPanel')
        }
        this.userMessage = ''
        this.initializeAutoExpand(document.getElementById('input_chat'))
      }

      this.isAudioInputToggle = !this.isAudioInputToggle
      return false
    },
    clipUserMessage(str) {
      if (str.length > config.COUNTER_SETTINGS.MAX_LENGTH) {
        this.alertMessage = this.$t('user_message_length_error')
        this.showAlertMessage()
        return str.substring(0, config.COUNTER_SETTINGS.MAX_LENGTH)
      }

      return str
    },
    showAlertMessage() {
      if (alertTimeout) {
        clearTimeout(alertTimeout)
      }

      alertTimeout = setTimeout(() => {
        this.alertMessage = ''
      }, config.ALERT_DELAY)
    },
    checkFile(file) {
      // If the file size is greater than max upload limit (10 mb) then return
      if (file.size > config.MAX_UPLOAD_LIMIT) {
        this.alertMessage = this.$t('file_size_reaches_max_limit')
        return false
      }

      // If the file type not valid show error
      if (!config.ACCEPTABLE_FILE_TYPES.includes(file.type)) {
        this.alertMessage = this.$t('invalid_file_type')
        return false
      }

      return true
    },
    async fileUpload() {
      if (this.isSTTBusy || this.isSendingMessage) {
        return false
      }

      if (this.isRatingPanelDisplayed) {
        this.$emit('hideRatingPanel')
      }

      const files = await fileDialog({ accept: config.ACCEPTABLE_FILE_TYPES })

      if (!files.length) {
        return
      }

      const file = files[0]

      if (!this.checkFile(file)) {
        this.showAlertMessage()
        return
      }

      this.$store.dispatch('set_input_state', false)
      this.$store.dispatch('socket_fileUpload', { file, isDisplayedProgressBar: file.size > config.MAX_PROGRESS_LIMIT })
    },
    abortUpload() {
      // File upload will be stopped, we display back the input section
      this.$store.dispatch('abort_fileUpload')
    },
    getNumberOfInputIcons() {
      if (this.showFileUploadIcon && this.shortcutMenu.length) {
        return 2
      }

      return this.showFileUploadIcon || this.shortcutMenu.length ? 1 : 0
    },
    /**
     * Handle the enter key down event
     * On desktop, if shift is pressed, add a new line, otherwise send the message
     * On mobile, it will always add a new line (sending message will only possible with the send button)
     * @param {Event} event - The keydown event
     */
    handleEnterKeyDown(event) {
      if (!this.isMobile) {
        if (event.shiftKey) {
          this.userMessage += '\n'
        } else {
          event.preventDefault()
          this.sendMessage(this.inputTypes.KEYBOARD)
        }
      }
    }
  }
}
</script>
